import {parseAppZoneDate} from '@/utils/dateUtils'
import {useEffect, useRef} from 'react'

/**
 * THE FLIGHT HAS LANDED — fired from the trip's own flight record, not from a stopwatch.
 *
 * ── ⚠️ THIS REPLACES THE ARMED STORAGE RECORD, AND IT IS A BETTER SOURCE ──────────
 * The mobile apps write one timestamp at booking time and count down to it. That number
 * is a prediction made hours earlier and it cannot correct itself, so the one flight it
 * gets wrong is the delayed one — the passenger is told they have arrived while they are
 * still in the air. The backend now tracks the flight and puts the result on the trip
 * (`trip.flight`), so the portal can read the truth instead of guessing at it.
 *
 * ── ⚠️ TWO SIGNALS, AND NEITHER IS ENOUGH ALONE ───────────────────────────────────
 * `flight_status` is the honest one: when it says arrived, the aircraft is at the gate and
 * there is nothing to infer. But it is polled, so it lags; it is null until the first poll
 * lands; and tracking can STOP — a cancelled or diverted flight sets `stopped_reason` and
 * the status then never changes again. Waiting on it alone means a screen that, in exactly
 * those cases, never appears.
 *
 * `expected_landing_at` is the floor under that. It is the backend's current landing time,
 * re-stated as the flight is re-polled, so unlike the booking-time record it moves with a
 * delay. It fires when the moment passes, whatever the status says.
 *
 * Whichever comes first wins. Status is the precise answer; the timestamp is the one that
 * still answers when the status has stopped talking.
 *
 * ── ⚠️ NULL IS "NOT YET KNOWN", NEVER "NOT LANDED" ────────────────────────────────
 * A freshly booked trip has `last_polled_at: null` and every field below it null. Reading
 * that as "has not landed" is correct by luck; reading a null STATUS as a decision is how
 * a trigger ends up firing on nothing.
 *
 * @param {object|null} trip  the trip resource, carrying `flight`
 * @param {Function} onArrived  called once, with the trip, when it has landed
 */

/**
 * Statuses that mean the aircraft is down and the passenger is walking.
 *
 * ⚠️ MATCHED CASE-INSENSITIVELY AND LOOSELY ON PURPOSE. The exact vocabulary is not in
 * api-docs and no other portal compares against it — the driver page only prints it — so
 * this is the provider's own set as far as it is known. An unrecognised status costs
 * nothing: `expected_landing_at` still fires.
 */
const ARRIVED_STATUSES = new Set(['arrived', 'landed'])

export function hasFlightLanded(trip) {
    const status = trip?.flight?.flight_status
    return typeof status === 'string' && ARRIVED_STATUSES.has(status.trim().toLowerCase())
}

/** The moment the welcome screen is due, or null while the flight has not been polled. */
export function expectedLandingTime(trip) {
    const raw = trip?.flight?.expected_landing_at
    if (!raw) return null
    // 'Y-m-d H:i:s' from the API is UTC; a bare space-separated string is parsed as LOCAL
    // by every browser, which on this side of the world is hours of error in the direction
    // that fires the screen early.
    const iso = String(raw).trim().replace(' ', 'T')
    const at = new Date(/[Zz]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`)
    return Number.isNaN(at.getTime()) ? null : at
}

/**
 * HAS THE MOMENT TO WALK PASSED? — the same question the trigger answers, asked as a
 * boolean by screens that did not navigate here on it.
 *
 * ⚠️ THREE SOURCES, WEAKEST LAST, AND THE ORDER IS THE POINT. A confirmed landing is
 * the truth. Failing that, the backend's current landing estimate — which moves with a
 * delay, unlike a booking-time prediction. Failing THAT, the pickup time itself: a trip
 * whose pickup hour has come and gone belongs to a passenger who is on the ground, whatever
 * the flight record does or does not say.
 *
 * ⚠️ AND IT COSTS NOTHING. The mobile app answers this with a direct, uncached,
 * billed-per-call AeroDataBox request from the device. Every field read here is already on
 * the trip payload the screen has, put there by the backend's own flight polling — which
 * is also the only place a flight-provider key belongs.
 */
export function flightLandedPassed(trip) {
    if (!trip) return false
    if (hasFlightLanded(trip)) return true

    const now = Date.now()
    const landing = expectedLandingTime(trip)
    if (landing && landing.getTime() <= now) return true

    /* Read through the app-zone parser: `scheduled_at` is Toronto wall clock with no zone,
       and a bare `new Date()` on it is wrong by the viewer's offset — which for a viewer
       west of Toronto means the pickup looks hours away when it has already passed. */
    const pickup = parseAppZoneDate(trip.scheduled_at)
    return !!pickup && pickup.getTime() <= now
}

export function useFlightArrivalTrigger(trip, onArrived) {
    const tripId = trip?.id ?? null
    const landed = hasFlightLanded(trip)
    const dueAt = expectedLandingTime(trip)
    const dueMs = dueAt ? dueAt.getTime() : null

    /* ⚠️ ONCE PER TRIP. Without it a re-render navigates again, onto a screen the
       passenger may have deliberately left. A ref so it survives renders without causing
       one — the same guard the mobile armer keeps as `lastHandledTripId`. */
    const handledRef = useRef(null)
    const timerRef = useRef(null)

    /* Through a ref so an inline callback does not re-arm the timer every render; the
       callback's identity is not what decides when this fires. */
    const triggerRef = useRef(onArrived)
    useEffect(() => { triggerRef.current = onArrived })

    useEffect(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }
        if (tripId == null) return undefined
        if (String(handledRef.current) === String(tripId)) return undefined

        const fire = () => {
            handledRef.current = tripId
            triggerRef.current?.(trip)
        }

        if (landed) {
            fire()
            return undefined
        }

        if (dueMs == null) return undefined

        const delay = dueMs - Date.now()

        /* ⚠️ A PASSED LANDING FIRES NOW rather than being discarded. The ordinary case is
           a tab that was closed while the flight was in the air: by the time the page is
           opened the moment has gone by and the passenger is standing in an arrivals
           hall, which is precisely when this is wanted. */
        if (delay <= 0) {
            fire()
            return undefined
        }

        timerRef.current = setTimeout(fire, delay)
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current)
            timerRef.current = null
        }
        // `trip` is read fresh inside `fire`; re-running on its identity would re-arm the
        // timer on every poll response, even one that changed nothing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tripId, landed, dueMs])
}

export default useFlightArrivalTrigger
